import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useApi } from '../hooks/useApi';

// ─── Storage ───────────────────────────────────────────────────────────────────
// Legacy localStorage key — read once on first API load, push to DB, then deleted.
const LEGACY_STORAGE_KEY = 'ai-projects-v1';
function loadLegacyProjects() {
  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch { return null; }
}

// ─── Constants ─────────────────────────────────────────────────────────────────
const STATUSES  = ['Briefed', 'Scoping', 'In Progress', 'Testing', 'Live', 'On Hold', 'Cancelled'];
const PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];
const FREQUENCIES = ['Daily', 'Weekly', 'Fortnightly', 'Monthly', 'Quarterly', 'Ad-hoc'];

const FREQ_PER_MONTH = {
  Daily: 21, Weekly: 4.33, Fortnightly: 2.17, Monthly: 1, Quarterly: 0.33, 'Ad-hoc': 1,
};

// MAGIC-aligned status colour tokens
const STATUS_COLORS = {
  Briefed:       'var(--text-muted)',
  Scoping:       '#818CF8',
  'In Progress': 'var(--warn)',
  Testing:       '#A78BFA',
  Live:          'var(--success)',
  'On Hold':     'var(--text-muted)',
  Cancelled:     'var(--danger)',
};

const PRIORITY_COLORS = {
  Low:      'var(--text-muted)',
  Medium:   '#818CF8',
  High:     'var(--warn)',
  Critical: 'var(--danger)',
};

// ─── Helpers ───────────────────────────────────────────────────────────────────
let _idSeq = 0;
function mkId() { return `aip-${Date.now()}-${++_idSeq}`; }

function fmtHours(h) {
  if (h == null || isNaN(h)) return '—';
  if (h >= 1000) return `${(h / 1000).toFixed(1)}k hrs`;
  return `${h.toFixed(1)} hrs`;
}

function hoursSavedPerMonth(p) {
  const freq = FREQ_PER_MONTH[p.frequency] || 0;
  const saved = Math.max(0, (Number(p.current_hours) || 0) - (Number(p.automated_hours) || 0));
  return freq * saved;
}

function defaultProgressForStatus(status) {
  switch (status) {
    case 'Briefed':     return 10;
    case 'Scoping':     return 25;
    case 'In Progress': return 50;
    case 'Testing':     return 80;
    case 'Live':        return 100;
    default:            return 0;
  }
}

const TODAY = new Date().toISOString().slice(0, 10);

const BLANK_PROJECT = {
  title: '', requester: '', owner: '', description: '',
  links: [{ label: '', url: '' }],
  current_hours: '', automated_hours: '', frequency: 'Weekly',
  priority: 'Medium', status: 'Briefed', progress: 10,
  target_date: '', notes: '', created: TODAY,
};

// ─── UI atoms (CSS-variable driven, MAGIC-style) ───────────────────────────────
function Chip({ text, color = 'var(--text-muted)', tone = 'soft' }) {
  if (!text) return null;
  // Accept either hex (#RRGGBB) or CSS var (var(--info)); compute soft bg + border
  // accordingly. Hex uses alpha-hex string concat; var uses color-mix.
  const isVar = typeof color === 'string' && color.startsWith('var(');
  const bg = tone === 'solid'
    ? color
    : isVar ? `color-mix(in srgb, ${color} 10%, transparent)` : `${color}1a`;
  const br = isVar ? `color-mix(in srgb, ${color} 33%, transparent)` : `${color}55`;
  const fg = tone === 'solid' ? '#fff' : color;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      fontSize: 11, fontWeight: 600, lineHeight: 1,
      color: fg, background: bg, border: `1px solid ${br}`,
      borderRadius: 999, padding: '4px 10px', whiteSpace: 'nowrap',
    }}>
      {text}
    </span>
  );
}

function FilterPill({ text, active, onClick }) {
  return (
    <button onClick={onClick}
      style={{
        fontSize: 12, fontWeight: active ? 600 : 500,
        color: active ? 'var(--text-primary)' : 'var(--text-muted)',
        background: active ? 'var(--bg-elevated)' : 'transparent',
        border: `1px solid ${active ? 'var(--border-soft)' : 'var(--border)'}`,
        borderRadius: 999, padding: '5px 12px', cursor: 'pointer',
        transition: 'all 0.15s',
      }}>
      {text}
    </button>
  );
}

function ProgressBar({ value, status }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  const color = STATUS_COLORS[status] || 'var(--accent)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ flex: 1, minWidth: 60, height: 5, background: 'var(--bg-input)', borderRadius: 999, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 999, transition: 'width 0.25s' }} />
      </div>
      <span style={{ fontSize: 11, color, fontWeight: 600, minWidth: 32, textAlign: 'right' }}>{pct}%</span>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', options, rows, placeholder, hint }) {
  const base = {
    background: 'var(--bg-input)', border: '1px solid var(--border-soft)',
    borderRadius: 6, color: 'var(--text-primary)', fontSize: 13,
    padding: '7px 10px', width: '100%', boxSizing: 'border-box',
    fontFamily: 'inherit',
  };
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, marginBottom: 6 }}>{label}</div>
      {options ? (
        <select value={value ?? ''} onChange={e => onChange(e.target.value)} style={base}>
          {options.map(o => <option key={o} value={o}>{o || '(none)'}</option>)}
        </select>
      ) : type === 'textarea' ? (
        <textarea value={value ?? ''} onChange={e => onChange(e.target.value)} rows={rows || 3} placeholder={placeholder}
          style={{ ...base, resize: 'vertical' }} />
      ) : (
        <input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={base} />
      )}
      {hint && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{hint}</div>}
    </label>
  );
}

function PrimaryButton({ children, onClick, disabled, tone = 'primary' }) {
  const tones = {
    primary: { bg: 'var(--accent)', fg: '#fff', border: 'var(--accent)' },
    ghost:   { bg: 'transparent',   fg: 'var(--text-body)', border: 'var(--border-soft)' },
    danger:  { bg: 'transparent',   fg: 'var(--danger)', border: 'var(--danger-border)' },
  };
  const t = tones[tone];
  return (
    <button onClick={onClick} disabled={disabled}
      style={{
        background: t.bg, color: t.fg, border: `1px solid ${t.border}`,
        borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1, transition: 'all 0.15s',
      }}>
      {children}
    </button>
  );
}

// ─── Edit form ─────────────────────────────────────────────────────────────────
function ProjectEditForm({ project, onSave, onDelete, onClose }) {
  const [form, setForm] = useState({
    ...BLANK_PROJECT, ...project,
    links: (project.links && project.links.length) ? project.links : [{ label: '', url: '' }],
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const num = (k, v) => set(k, v === '' ? '' : Number(v));

  function setLink(idx, patch) { setForm(f => { const links = [...f.links]; links[idx] = { ...links[idx], ...patch }; return { ...f, links }; }); }
  function addLink() { setForm(f => ({ ...f, links: [...f.links, { label: '', url: '' }] })); }
  function removeLink(idx) { setForm(f => ({ ...f, links: f.links.filter((_, i) => i !== idx) })); }

  function handleStatus(v) {
    setForm(f => {
      const auto = defaultProgressForStatus(v);
      const prog = (f.progress === defaultProgressForStatus(f.status)) ? auto : f.progress;
      return { ...f, status: v, progress: prog };
    });
  }

  const savedPerMonth = hoursSavedPerMonth(form);

  function handleSave() {
    const clean = {
      ...form,
      links: form.links.filter(l => l.url && l.url.trim()),
      current_hours: form.current_hours === '' ? 0 : Number(form.current_hours),
      automated_hours: form.automated_hours === '' ? 0 : Number(form.automated_hours),
      progress: Number(form.progress) || 0,
    };
    onSave(clean);
  }

  const linkInput = {
    background: 'var(--bg-input)', border: '1px solid var(--border-soft)',
    borderRadius: 6, color: 'var(--text-primary)', fontSize: 12,
    padding: '6px 10px', fontFamily: 'inherit',
  };

  return (
    <div style={{
      padding: 24, background: 'var(--bg-elevated)',
      borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)',
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 16 }}>
        <div style={{ gridColumn: 'span 2' }}>
          <Field label="Project title *" value={form.title} onChange={v => set('title', v)} placeholder="e.g. Automate weekly scan report" />
        </div>
        <Field label="Requester *" value={form.requester} onChange={v => set('requester', v)} placeholder="Team member" />
        <Field label="Owner / builder" value={form.owner} onChange={v => set('owner', v)} placeholder="Who's delivering" />
      </div>

      <div style={{ marginBottom: 16 }}>
        <Field label="Workflow brief — what's the task? trigger? output?" value={form.description}
          onChange={v => set('description', v)} type="textarea" rows={4}
          placeholder="Describe the manual workflow: inputs, decisions made, outputs, who uses the result." />
      </div>

      {/* Links */}
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 8, padding: 14, marginBottom: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>
            Links to folders / files / references
          </div>
          <button onClick={addLink}
            style={{
              background: 'transparent', border: '1px solid var(--border-soft)',
              borderRadius: 6, color: 'var(--text-body)', fontSize: 12,
              padding: '4px 10px', cursor: 'pointer', fontWeight: 500,
            }}>
            + Add link
          </button>
        </div>
        {form.links.map((l, idx) => (
          <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 8, marginBottom: 8 }}>
            <input placeholder="Label (e.g. Source folder)" value={l.label}
              onChange={e => setLink(idx, { label: e.target.value })} style={linkInput} />
            <input placeholder="https://… or file path" value={l.url}
              onChange={e => setLink(idx, { url: e.target.value })} style={linkInput} />
            <button onClick={() => removeLink(idx)} disabled={form.links.length === 1 && !l.url}
              style={{
                background: 'transparent', border: '1px solid var(--border-soft)',
                borderRadius: 6, color: 'var(--text-muted)', fontSize: 14,
                padding: '4px 12px', cursor: 'pointer',
              }}>
              ✕
            </button>
          </div>
        ))}
      </div>

      {/* Time-saved block */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16,
        padding: 14, background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 8, marginBottom: 16,
      }}>
        <Field label="Current hours / run" value={form.current_hours} onChange={v => num('current_hours', v)}
          type="number" hint="Manual effort per occurrence" />
        <Field label="Hours / run after automation" value={form.automated_hours} onChange={v => num('automated_hours', v)}
          type="number" hint="Residual manual effort" />
        <Field label="Frequency" value={form.frequency} onChange={v => set('frequency', v)} options={FREQUENCIES} />
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, marginBottom: 6 }}>
            Est. hours saved / month
          </div>
          <div style={{
            background: 'var(--success-soft)', border: '1px solid var(--success-border)', borderRadius: 'var(--radius)',
            padding: '7px 12px', color: 'var(--success)', fontSize: 15, fontWeight: 700,
          }}>
            {fmtHours(savedPerMonth)}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            ≈ {fmtHours(savedPerMonth * 12)} / year
          </div>
        </div>
      </div>

      {/* Status / priority / progress */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 16 }}>
        <Field label="Status" value={form.status} onChange={handleStatus} options={STATUSES} />
        <Field label="Priority" value={form.priority} onChange={v => set('priority', v)} options={PRIORITIES} />
        <Field label="Target go-live" value={form.target_date} onChange={v => set('target_date', v)} type="date" />
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, marginBottom: 6 }}>
            Progress — {form.progress}%
          </div>
          <input type="range" min={0} max={100} step={5} value={form.progress}
            onChange={e => set('progress', Number(e.target.value))} style={{ width: '100%' }} />
          <div style={{ marginTop: 6 }}>
            <ProgressBar value={form.progress} status={form.status} />
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <Field label="Notes / blockers / next steps" value={form.notes} onChange={v => set('notes', v)}
          type="textarea" rows={2} />
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        {onDelete && <PrimaryButton tone="danger" onClick={onDelete}>Delete</PrimaryButton>}
        <PrimaryButton tone="ghost" onClick={onClose}>Cancel</PrimaryButton>
        <PrimaryButton onClick={handleSave} disabled={!form.title || !form.requester}>Save project</PrimaryButton>
      </div>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export default function AIProjects() {
  const apiFetch = useApi();
  const [projects, setProjects] = useState([]);
  const [statusFilter, setStatusFilter] = useState('All');
  const [priorityFilter, setPriorityFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [savedToast, setSavedToast] = useState(false);

  // Migration machinery — same recipe as Phases 2-4.
  const hasHydrated = useRef(false);
  const lastSavedJsonRef = useRef(null);

  const {
    data: serverData,
    error: serverError,
    refetch: refetchProjects,
    isFetching: isLoadingProjects,
  } = useQuery({
    queryKey: ['ai-projects'],
    queryFn: async () => {
      const res = await apiFetch('/ai-projects');
      if (!res.ok) throw new Error(`Failed to load ai-projects: ${res.status}`);
      return res.json();
    },
    refetchOnMount: 'always',
    // Surface hard failures immediately (default 3× retry hides them ~6s).
    // Without this, hydration silently fails and the save effect
    // short-circuits forever, with no visible feedback.
    retry: false,
  });

  const saveProjects = useMutation({
    mutationFn: async (next) => {
      const res = await apiFetch('/ai-projects', {
        method: 'PUT',
        body: JSON.stringify({ projects: next }),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`Save failed: HTTP ${res.status} ${body.slice(0, 200)}`);
      }
      return res.json();
    },
    onError: (err) => {
      // eslint-disable-next-line no-console
      console.error('[AIProjects] save error', err);
    },
  });
  const mutateRef = useRef(saveProjects.mutate);
  mutateRef.current = saveProjects.mutate;

  // Hydrate (runs once).
  useEffect(() => {
    if (hasHydrated.current || !serverData) return;
    hasHydrated.current = true;

    if (Array.isArray(serverData.projects)) {
      setProjects(serverData.projects);
      lastSavedJsonRef.current = JSON.stringify(serverData.projects);
      return;
    }

    const legacy = loadLegacyProjects();
    if (legacy) {
      setProjects(legacy);
      lastSavedJsonRef.current = JSON.stringify(legacy);
      mutateRef.current(legacy, {
        onSuccess: () => {
          try { localStorage.removeItem(LEGACY_STORAGE_KEY); } catch { /* ignore */ }
        },
      });
    } else {
      lastSavedJsonRef.current = JSON.stringify([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverData]);

  // Debounced save.
  const saveTimer = useRef(null);
  useEffect(() => {
    if (!hasHydrated.current) return;
    const currentJson = JSON.stringify(projects);
    if (currentJson === lastSavedJsonRef.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      mutateRef.current(projects);
      lastSavedJsonRef.current = currentJson;
    }, 1000);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [projects]);

  function showSaved() { setSavedToast(true); setTimeout(() => setSavedToast(false), 1800); }

  function upsert(project) {
    setProjects(prev => {
      const i = prev.findIndex(p => p._id === project._id);
      if (i >= 0) { const c = [...prev]; c[i] = project; return c; }
      return [project, ...prev];
    });
    showSaved();
  }

  function removeProject(id) {
    setProjects(prev => prev.filter(p => p._id !== id));
    setExpanded(null);
    setAddingNew(false);
  }

  function handleAddNew() {
    const draft = { ...BLANK_PROJECT, _id: mkId() };
    setProjects(prev => [draft, ...prev]);
    setExpanded(draft._id);
    setAddingNew(true);
    setStatusFilter('All'); setPriorityFilter('All'); setSearch('');
  }

  function handleCancel(id) {
    if (addingNew) { removeProject(id); setAddingNew(false); }
    else setExpanded(null);
  }

  function handleExport() {
    const blob = new Blob([JSON.stringify(projects, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `ai-projects-${TODAY}.json`; a.click();
    URL.revokeObjectURL(url);
  }

  const metrics = useMemo(() => {
    const live   = projects.filter(p => p.status === 'Live');
    const active = projects.filter(p => !['Live', 'Cancelled', 'On Hold'].includes(p.status));
    const totalSavedMonth = projects.filter(p => p.status !== 'Cancelled').reduce((s, p) => s + hoursSavedPerMonth(p), 0);
    const liveSavedMonth  = live.reduce((s, p) => s + hoursSavedPerMonth(p), 0);
    const avgProgress     = active.length ? active.reduce((s, p) => s + (Number(p.progress) || 0), 0) / active.length : 0;
    return { total: projects.length, live: live.length, active: active.length, totalSavedMonth, liveSavedMonth, avgProgress };
  }, [projects]);

  const filtered = useMemo(() => {
    let list = projects;
    if (statusFilter   !== 'All') list = list.filter(p => p.status   === statusFilter);
    if (priorityFilter !== 'All') list = list.filter(p => p.priority === priorityFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        (p.title||'').toLowerCase().includes(q) ||
        (p.requester||'').toLowerCase().includes(q) ||
        (p.owner||'').toLowerCase().includes(q) ||
        (p.description||'').toLowerCase().includes(q)
      );
    }
    const statusRank = { 'In Progress': 0, Testing: 1, Scoping: 2, Briefed: 3, Live: 4, 'On Hold': 5, Cancelled: 6 };
    const prioRank   = { Critical: 0, High: 1, Medium: 2, Low: 3 };
    return [...list].sort((a, b) => {
      const sa = statusRank[a.status] ?? 9, sb = statusRank[b.status] ?? 9;
      if (sa !== sb) return sa - sb;
      const pa = prioRank[a.priority] ?? 9, pb = prioRank[b.priority] ?? 9;
      if (pa !== pb) return pa - pb;
      return (Number(b.progress) || 0) - (Number(a.progress) || 0);
    });
  }, [projects, statusFilter, priorityFilter, search]);

  return (
    <div style={{ maxWidth: 1440, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Load-failure banner: hydration never completed, so edits won't save.
          Matches the CRM.jsx Phase 14 fix. */}
      {serverError && !hasHydrated.current && (
        <div style={{ background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 'var(--radius)', color: 'var(--danger)', padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13 }}>
            <strong>Could not load your AI projects.</strong>{' '}
            <span>Any edits you make below will <u>not be saved</u> until this loads successfully.</span>
            <div style={{ fontSize: 11, marginTop: 4, opacity: 0.85 }}>
              {String(serverError?.message || serverError).slice(0, 200)}
            </div>
          </div>
          <button onClick={() => refetchProjects()} disabled={isLoadingProjects}
            style={{ background: 'var(--bg-card)', border: '1px solid var(--danger)', color: 'var(--danger)', borderRadius: 'var(--radius)', padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: isLoadingProjects ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
            {isLoadingProjects ? 'Retrying…' : 'Retry'}
          </button>
        </div>
      )}

      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ color: 'var(--text-primary)', fontSize: 20, fontWeight: 700, margin: 0, letterSpacing: '-0.01em' }}>
            AI Projects & Workflow Automation
          </h2>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
            Team briefing board — brief workflows, estimate time saved, track delivery.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {savedToast && <span style={{ fontSize: 12, color: 'var(--success)', fontWeight: 500 }}>Saved ✓</span>}
          {projects.length > 0 && <PrimaryButton tone="ghost" onClick={handleExport}>Export JSON</PrimaryButton>}
          <PrimaryButton onClick={handleAddNew}>+ Brief new workflow</PrimaryButton>
        </div>
      </div>

      {/* KPI strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
        {[
          { label: 'Total projects',         value: metrics.total,                             sub: `${metrics.active} active`,                                   color: 'var(--text-primary)' },
          { label: 'Live automations',       value: metrics.live,                              sub: `${fmtHours(metrics.liveSavedMonth)} saved / month`,          color: 'var(--success)' },
          { label: 'Potential hrs / month',  value: fmtHours(metrics.totalSavedMonth),         sub: `≈ ${fmtHours(metrics.totalSavedMonth * 12)} / year`,         color: 'var(--warn)' },
          { label: 'Avg progress (active)',  value: `${metrics.avgProgress.toFixed(0)}%`,      sub: metrics.active ? `${metrics.active} in flight` : 'no active', color: '#818CF8' },
        ].map(k => (
          <div key={k.label} className="kpi-card" style={{ padding: '18px 20px' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10, fontWeight: 500 }}>
              {k.label}
            </div>
            <div style={{ fontSize: 26, fontWeight: 700, color: k.color, letterSpacing: '-0.02em' }}>{k.value}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="card" style={{ padding: 14, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          placeholder="Search title, requester, description…"
          value={search} onChange={e => setSearch(e.target.value)}
          style={{
            background: 'var(--bg-input)', border: '1px solid var(--border-soft)',
            borderRadius: 8, color: 'var(--text-primary)', fontSize: 13,
            padding: '7px 12px', width: 280, fontFamily: 'inherit',
          }}
        />
        <div style={{ width: 1, height: 20, background: 'var(--border)' }} />
        <span style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 500 }}>Status</span>
        {['All', ...STATUSES].map(s =>
          <FilterPill key={s} text={s} active={statusFilter === s} onClick={() => setStatusFilter(s)} />
        )}
        <div style={{ width: 1, height: 20, background: 'var(--border)' }} />
        <span style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 500 }}>Priority</span>
        {['All', ...PRIORITIES].map(p =>
          <FilterPill key={p} text={p} active={priorityFilter === p} onClick={() => setPriorityFilter(p)} />
        )}
        <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: 12 }}>
          {filtered.length} of {projects.length}
        </span>
      </div>

      {/* Empty state */}
      {projects.length === 0 && (
        <div className="card" style={{ padding: 56, textAlign: 'center', borderStyle: 'dashed' }}>
          <div style={{ color: 'var(--text-primary)', fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
            No AI projects briefed yet
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20, maxWidth: 520, margin: '0 auto 20px' }}>
            Kick off by briefing a workflow your team wants to automate. Include links to folders/files, estimate current effort, and track delivery progress.
          </div>
          <PrimaryButton onClick={handleAddNew}>+ Brief first workflow</PrimaryButton>
        </div>
      )}

      {/* Project cards */}
      {projects.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map(p => {
            const isExp = expanded === p._id;
            const saved = hoursSavedPerMonth(p);
            return (
              <div key={p._id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {/* Card header row — clickable */}
                <div onClick={() => setExpanded(isExp ? null : p._id)}
                  style={{
                    padding: '16px 20px', cursor: 'pointer',
                    display: 'grid',
                    gridTemplateColumns: 'minmax(260px, 3fr) minmax(160px, 1.4fr) 1fr 1fr 180px',
                    gap: 16, alignItems: 'center',
                    background: isExp ? 'var(--bg-elevated)' : 'transparent',
                    transition: 'background 0.15s',
                  }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600 }}>
                        {p.title || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontWeight: 400 }}>(untitled)</span>}
                      </span>
                      <Chip text={p.status} color={STATUS_COLORS[p.status]} />
                      <Chip text={p.priority} color={PRIORITY_COLORS[p.priority]} />
                    </div>
                    {p.description && (
                      <div style={{
                        fontSize: 12, color: 'var(--text-muted)',
                        overflow: 'hidden', textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap', maxWidth: 560,
                      }}>
                        {p.description}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 14, marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>
                      {p.frequency && <span>↻ {p.frequency}</span>}
                      {p.links && p.links.length > 0 && <span style={{ color: 'var(--accent)' }}>🔗 {p.links.length} link{p.links.length > 1 ? 's' : ''}</span>}
                      {p.target_date && <span>🎯 {p.target_date}</span>}
                    </div>
                  </div>

                  <div style={{ fontSize: 12 }}>
                    <div style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                      {p.requester || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </div>
                    {p.owner && <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>→ {p.owner}</div>}
                  </div>

                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                      Saved / mo
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: saved > 0 ? 'var(--success)' : 'var(--text-muted)' }}>
                      {saved > 0 ? fmtHours(saved) : '—'}
                    </div>
                  </div>

                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
                      Saved / yr
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: saved > 0 ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                      {saved > 0 ? fmtHours(saved * 12) : '—'}
                    </div>
                  </div>

                  <div style={{ minWidth: 160 }}>
                    <ProgressBar value={p.progress} status={p.status} />
                  </div>
                </div>

                {isExp && (
                  <ProjectEditForm
                    project={p}
                    onSave={updated => { upsert({ ...updated, _id: p._id }); setAddingNew(false); setExpanded(null); }}
                    onDelete={() => { if (window.confirm(`Delete "${p.title || 'this project'}"?`)) removeProject(p._id); }}
                    onClose={() => handleCancel(p._id)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
